import { useEffect, useState } from "react";
import _ from "lodash";
import * as XLSX from "xlsx";

import Stack from "components/Stack";
import Table, { TableBody, TableCell, TableRow } from "components/Table";
import Tabs, { Tab } from "components/Tabs";
import { useLoadingBackdrop } from "components/useLoadingBackdrop";

export interface IExcelEmbed {
  sourceUrl?: string;
}

const ExcelEmbed = ({ sourceUrl }: IExcelEmbed) => {
  const [data, setData] = useState<Array<Array<any>>>([]);
  const [workbook, setWorkbook] = useState<XLSX.WorkBook | null>(null);
  const [activeSheetIndex, setActiveSheetIndex] = useState(0);
  const [showLoading, hideLoading] = useLoadingBackdrop();

  useEffect(() => {
    const fetchExcel = async () => {
      if (_.isNil(sourceUrl)) {
        return;
      }

      try {
        const response = await fetch(sourceUrl);
        const blob = await response.blob();
        const reader = new FileReader();

        reader.onload = (e) => {
          if (!e.target?.result) return;

          const arrayBuffer = e.target.result as ArrayBuffer;
          const workbook = XLSX.read(new Uint8Array(arrayBuffer), {
            type: "array",
            dense: true,
            sheetRows: 100000,
          });

          setWorkbook(workbook);
        };

        reader.readAsArrayBuffer(blob);
      } catch (error) {
        console.error("Error fetching Excel file:", error);
      }
    };

    fetchExcel();
  }, [sourceUrl]);

  useEffect(() => {
    if (!_.isNil(workbook)) {
      const sheetName = workbook.SheetNames[activeSheetIndex];
      const sheet = workbook.Sheets[sheetName];

      showLoading();
      // Convert to JSON
      const jsonData = XLSX.utils.sheet_to_json(sheet, {
        header: 1,
        defval: "",
        raw: false,
      });

      setData(jsonData as Array<Array<any>>);

      hideLoading();
    }
  }, [workbook, activeSheetIndex, showLoading, hideLoading]);

  return (
    <>
      <Tabs value={activeSheetIndex}>
        {workbook?.SheetNames.map((sheetName, index) => (
          <Tab
            key={index}
            value={index}
            label={sheetName}
            onClick={() => setActiveSheetIndex(index)}
          />
        ))}
      </Tabs>
      <Stack overflow="auto" sx={{ maxHeight: "calc(100% - 100px)" }}>
        <Table key={activeSheetIndex}>
          <TableBody>
            {data.map((row, rowIndex) => (
              <TableRow key={rowIndex}>
                {row.map((cell, cellIndex: number) => (
                  <TableCell key={cellIndex}>{cell}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Stack>
    </>
  );
};

export default ExcelEmbed;
